import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Button} from 'antd';
import {PlusOutlined} from '@ant-design/icons';
import PropTypes from 'prop-types';
import {StyledVendorsTableContainer, StyledVendorsTableSpace, StyledVendorsTableAmountSpace} from './StyledVendorsTable';
import {
  StyledTableSettingsButtonDivider,
  StyledTableSettingsButtonSpace
} from '../../SubscriptionsPage/TableSettingsButton/StyledTableSettingsButton';
import NoSearchResults from '../../../NoSearchResults';
import Table from '../../../../components/Table';
import TableFilters from '../../../../components/pages/VendorsPage/TableFilters';
import {
  amountHelpers,
  elementHelpers,
  localizationHelpers,
  objectHelpers,
  subscriptionsHelpers,
  tableHelpers,
  transactionsHelpers,
  vendorsHelpers
} from '../../../../utils/helpers';
import {ChevronDownIcon, ChevronUpIcon} from '../../../../icons';
import {ReactComponent as EmptySearchResultSvgImage} from '../../../../static/images/pages/vendors/empty-search-result.svg';

const alwaysAvailableColumnKeys = ['name', 'priority', 'type'];

const defaultSelectedColumns = ['name', 'country', 'priority', 'type', 'category', 'spending_month', 'spending_year', 'total_outstanding_invoices', 'type', 'status'];

const amountColumnProps = {
  align: 'right',
  sorter: true,
  sortDirections: ['descend', 'ascend'],
};

const VendorsTable = ({
  loading = false,
  onAddClick,
  onFilter,
  onPaginate,
  onRowClick,
  onStatusChange,
  onTableChange,
  selectedRowIndex,
  tableData = {},
  tableQuery = {},
  ...rest
}) => {
  const [t] = useTranslation(['main', 'vendors']);
  const [selectedColumns, setSelectedColumns] = useState(vendorsHelpers.getDefaultVendorsTableColumns(defaultSelectedColumns));

  const { showEmptyState, showNoResults } = useMemo(() => {
    const hasFilters = !objectHelpers.isEmptyObject(
      (({ order_by, ...rest }) => rest)(tableQuery)
    );
    const hasRows = Array.isArray(tableData?.rows) && tableData.rows.length > 0;

    return {
      showEmptyState: loading ? false : !hasRows && !hasFilters,
      showNoResults: !hasRows && hasFilters,
    };
  }, [tableQuery, tableData?.rows, loading]);

  const tablePagination = tableHelpers.getTablePagination({tableData, handleOnPaginate: onPaginate});

  const getAmountValue = (value) => (
    <span className='amount'>
      {amountHelpers.getAmountWithCurrencyCode(value || 0)}
    </span>
  );

  const getAmountWithIconValue = (value = 0, previousValue = 0) => {
    const hasChanged = value !== previousValue;
    const amount = getAmountValue(value);

    if (!hasChanged) {
      return amount;
    }

    const isNegative = value > previousValue;
    const Icon = isNegative ? ChevronUpIcon : ChevronDownIcon;

    return (
      <StyledVendorsTableAmountSpace size='small'>
        <span className={`icon ${isNegative ? 'negative' : 'positive'}`}>
          <Icon />
        </span>
        {amount}
      </StyledVendorsTableAmountSpace>
    );
  }

  const columns = [
    {
      dataIndex: 'name',
      sorter: true,
      title: t('name')
    },
    {
      dataIndex: 'country',
      render: localizationHelpers.getCountryFlagByName,
      title: t('country')
    },
    {
      dataIndex: 'priority',
      render: (value) => t(vendorsHelpers.getVendorPriority(value)),
      title: t('priority')
    },
    {
      dataIndex: 'type',
      render: (_, record) => vendorsHelpers.getVendorTypeIcon(record),
      title: t('type')
    },
    {
      dataIndex: 'category',
      render: vendorsHelpers.getVendorTags,
      title: t('category')
    },
    {
      ...amountColumnProps,
      dataIndex: 'spending_month',
      render: (_, record) => getAmountWithIconValue(record?.spending_month, record?.spending_last_month),
      title: `${t('paidMonth')} (€)`
    },
    {
      ...amountColumnProps,
      dataIndex: 'spending_year',
      render: (_, record) => getAmountWithIconValue(record?.spending_year, record?.spending_last_year),
      title: `${t('paidYear')} (€)`
    },
    {
      ...amountColumnProps,
      dataIndex: 'total_outstanding_invoices',
      render: getAmountValue,
      title: t('outstanding')
    },
    {
      dataIndex: 'status',
      render: (_, vendor) => vendorsHelpers.getVendorStatusMarkOrSelector({onChange: onStatusChange, vendor}),
      title: t('status')
    }
  ];

  const availableColumns = useMemo(() => subscriptionsHelpers.getAvailableColumns(columns, selectedColumns), [selectedColumns]); // eslint-disable-line react-hooks/exhaustive-deps

  const onChangeSelectedItems = (value) => {
    setSelectedColumns(value);
    vendorsHelpers.storeVendorsTableColumns(value);
  }

  const addNewButton = (
    <Button
      icon={<PlusOutlined />}
      onClick={onAddClick}
      size='large'
      type='primary'
    >
      {t('vendors')}
    </Button>
  );

  const settingsIcon = elementHelpers.getSettingsRightSideContent({
    alwaysAvailableColumnKeys,
    columns,
    rightSideContent: null,
    onChange: onChangeSelectedItems,
    selectedKeys: selectedColumns
  });

  const getRowClassName = (record,) => transactionsHelpers.getRowClassName({
    index: record.vendor_id,
    record,
    selectedRowIndex
  });

  const filtersRightSideContent = (
    <StyledTableSettingsButtonSpace
      size={0}
      split={<StyledTableSettingsButtonDivider type='vertical' />}
    >
      {settingsIcon}
      {addNewButton}
    </StyledTableSettingsButtonSpace>
  );

  return (
    <StyledVendorsTableContainer {...rest}>
      {!loading && showEmptyState && (
        <NoSearchResults
          image={<EmptySearchResultSvgImage />}
          spinning={loading}
          title={t('vendors:empty.title')}
        >
          {addNewButton}
        </NoSearchResults>
      )}
      {!showEmptyState && (
        <StyledVendorsTableSpace
          direction='vertical'
          size='large'
        >
          <TableFilters
            defaultQuery={tableQuery}
            onFilter={onFilter}
            rightSideContent={filtersRightSideContent}
          />
          {showNoResults ? (
            <NoSearchResults spinning={loading} />
          ) : (
            <Table
              columns={availableColumns}
              dataSource={tableData.rows}
              onChange={onTableChange}
              onRow={(record, index) => ({
                onClick: (e) => onRowClick && onRowClick(e, record, index)
              })}
              pagination={tablePagination}
              rowKey='vendor_id'
              rowClassName={getRowClassName}
            />
          )}
        </StyledVendorsTableSpace>
      )}
    </StyledVendorsTableContainer>
  );
}

VendorsTable.propTypes = {
  loading: PropTypes.bool,
  onAddClick: PropTypes.func,
  onFilter: PropTypes.func,
  onPaginate: PropTypes.func,
  onRowClick: PropTypes.func,
  onStatusChange: PropTypes.func,
  onTableChange: PropTypes.func,
  selectedRowIndex: PropTypes.string,
  tableData: PropTypes.object,
  tableQuery: PropTypes.object,
}

export default VendorsTable;
